/**
 * Foreclosure case ↔ license fuzzy match / link APIs.
 * Endpoints are not yet in the generated SDK.
 */

import { client } from "@/hera-api/client/client.gen";
import {
  getGovernmentForeclosureCaseReviewHistory,
  getOperatorForeclosureCaseReviewHistory,
} from "@/hera-api/client/sdk.gen";
import type { LicenseStatus } from "@/hera-api/client/types.gen";
import type { GovernmentForeclosureCaseListItem } from "@/hera-api/government-foreclosure-cases-api";
import type { ForeclosureCaseReviewStatus } from "@/lib/foreclosure-case-review-status";

export interface ForeclosureLicenseCandidate {
  license_id: string;
  license_display_id: string;
  status: LicenseStatus;
  effective_date: string;
  effective_end_date: string | null;
  is_deleted: boolean;
  property_apn: string | null;
  property_address1: string | null;
  property_address2: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zipcode: string | null;
  match_score: number;
  match_reasons: string[];
  existing_case_number: string | null;
  ams_display?: string | null;
  omt_display?: string | null;
}

export interface ForeclosureLicenseLinkContext {
  linked_license: ForeclosureLicenseCandidate | null;
  candidates: ForeclosureLicenseCandidate[];
}

/**
 * Assignment rows carry the full Cases-table row so shared columns (OMT, AMS,
 * registrant, review status) render the same way in the Assign Licenses tab.
 */
export interface ForeclosureLicenseAssignmentCandidate
  extends GovernmentForeclosureCaseListItem {
  suggested_match_is_multiple: boolean;
  suggested_match_already_linked: boolean;
  suggested_match_license_id: string | null;
  suggested_match_license_display_id: string | null;
  suggested_match_effective_date: string | null;
  suggested_match_omt_display: string | null;
  suggested_match_ams_display: string | null;
  suggested_match_registrant_display: string | null;
  suggested_match_address1: string | null;
  suggested_match_address2: string | null;
  suggested_match_city: string | null;
  suggested_match_state: string | null;
  suggested_match_zip: string | null;
  suggested_match_apn: string | null;
}

export interface ForeclosureLicenseAssignmentOptions {
  dayBuffer: number;
  matchOmt: boolean;
  matchAms: boolean;
  matchRegistrant: boolean;
}

export async function getForeclosureLicenseAssignmentCandidates(
  portal: "government" | "operator",
  query: {
    jurisdictionIds?: string[];
    page: number;
    pageSize: number;
    sortBy: string;
    sortDir: "asc" | "desc";
    filters?: Record<string, string[]>;
  } & ForeclosureLicenseAssignmentOptions,
): Promise<{
  cases: ForeclosureLicenseAssignmentCandidate[];
  total_count: number;
  page: number;
  page_size: number;
}> {
  if (portal === "operator" && !query.jurisdictionIds?.length) {
    return {
      cases: [],
      total_count: 0,
      page: query.page,
      page_size: query.pageSize,
    };
  }
  const params: Record<string, string | string[] | number | boolean> = {
    page: query.page,
    page_size: query.pageSize,
    sort_by: query.sortBy,
    sort_dir: query.sortDir,
    day_buffer: query.dayBuffer,
    match_omt: query.matchOmt,
    match_ams: query.matchAms,
    match_registrant: query.matchRegistrant,
  };
  if (query.jurisdictionIds?.length) {
    params.jurisdiction_ids = query.jurisdictionIds;
  }
  if (query.filters && Object.keys(query.filters).length > 0) {
    params.filters = JSON.stringify(query.filters);
  }
  const result = await client.get<{
    cases: ForeclosureLicenseAssignmentCandidate[];
    total_count: number;
    page: number;
    page_size: number;
  }>({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/license-assignment-candidates"
        : "/government/foreclosure-cases/license-assignment-candidates",
    query: params,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ??
        err.message ??
        "Failed to load license assignment candidates",
    );
  }
  return result.data as unknown as {
    cases: ForeclosureLicenseAssignmentCandidate[];
    total_count: number;
    page: number;
    page_size: number;
  };
}

export async function assignForeclosureLicenses(
  portal: "government" | "operator",
  assignments: Array<{
    countyFips: string;
    caseNumber: string;
    licenseId: string;
    jurisdictionId?: string | null;
  }>,
  options: ForeclosureLicenseAssignmentOptions,
): Promise<{
  success_count: number;
  error_count: number;
  results: Array<{
    county_fips: string;
    case_number: string;
    license_id: string;
    success: boolean;
    error: string | null;
  }>;
}> {
  const result = await client.post({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/assign-licenses"
        : "/government/foreclosure-cases/assign-licenses",
    body: {
      assignments: assignments.map((assignment) => ({
        county_fips: assignment.countyFips,
        case_number: assignment.caseNumber,
        license_id: assignment.licenseId,
        ...(assignment.jurisdictionId
          ? { jurisdiction_id: assignment.jurisdictionId }
          : {}),
      })),
      day_buffer: options.dayBuffer,
      match_omt: options.matchOmt,
      match_ams: options.matchAms,
      match_registrant: options.matchRegistrant,
    },
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to assign foreclosure licenses",
    );
  }
  return result.data as {
    success_count: number;
    error_count: number;
    results: Array<{
      county_fips: string;
      case_number: string;
      license_id: string;
      success: boolean;
      error: string | null;
    }>;
  };
}

export interface ForeclosureLicenseMatchQuery {
  caseNumber: string;
  apn?: string | null;
  propertyAddressFull?: string | null;
  propertyAddressCity?: string | null;
  propertyAddressState?: string | null;
  propertyAddressZip?: string | null;
  jurisdictionId?: string | null;
}

function buildMatchQuery(params: ForeclosureLicenseMatchQuery) {
  const query: Record<string, string> = {
    case_number: params.caseNumber,
  };
  if (params.apn) query.apn = params.apn;
  if (params.propertyAddressFull) {
    query.property_address_full = params.propertyAddressFull;
  }
  if (params.propertyAddressCity) {
    query.property_address_city = params.propertyAddressCity;
  }
  if (params.propertyAddressState) {
    query.property_address_state = params.propertyAddressState;
  }
  if (params.propertyAddressZip) {
    query.property_address_zip = params.propertyAddressZip;
  }
  if (params.jurisdictionId) {
    query.jurisdiction_id = params.jurisdictionId;
  }
  return query;
}

export async function getForeclosureLicenseMatches(
  portal: "government" | "operator",
  params: ForeclosureLicenseMatchQuery,
): Promise<ForeclosureLicenseLinkContext> {
  if (portal === "operator" && !params.jurisdictionId) {
    throw new Error("jurisdictionId is required for operator license matches");
  }

  const result = await client.get<ForeclosureLicenseLinkContext>({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/license-matches"
        : "/government/foreclosure-cases/license-matches",
    query: buildMatchQuery(params),
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to load license matches",
    );
  }

  return result.data as ForeclosureLicenseLinkContext;
}

export interface ForeclosureCaseReview {
  county_fips: string;
  case_number: string;
  is_reviewed: boolean;
  review_status: ForeclosureCaseReviewStatus | null;
  review_comment: string | null;
  override_omt_party_id?: string | null;
  override_ams_party_id?: string | null;
  override_registrant_account_id?: string | null;
  override_start_date?: string | null;
  effective_start_date?: string | null;
  is_start_date_overridden?: boolean;
  override_case_number?: string | null;
  effective_case_number?: string | null;
  is_case_number_overridden?: boolean;
  review_updated_at?: string | null;
  review_updated_by_display?: string | null;
}

export interface ForeclosureCaseReviewHistoryEvent {
  changed_at: string;
  changed_by_display?: string | null;
  review_status?: ForeclosureCaseReviewStatus | null;
  review_comment?: string | null;
}

export async function getForeclosureCaseReviewHistory(
  portal: "government" | "operator",
  params: { countyFips: string; caseNumber: string },
): Promise<ForeclosureCaseReviewHistoryEvent[]> {
  const result =
    portal === "operator"
      ? await getOperatorForeclosureCaseReviewHistory({
          query: {
            county_fips: params.countyFips,
            case_number: params.caseNumber,
          },
        })
      : await getGovernmentForeclosureCaseReviewHistory({
          query: {
            county_fips: params.countyFips,
            case_number: params.caseNumber,
          },
        });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ??
        err.message ??
        "Failed to load foreclosure case review history",
    );
  }

  return result.data?.events ?? [];
}

export async function suggestForeclosureCaseParties(
  portal: "government" | "operator",
  body: {
    countyFips: string;
    caseNumber: string;
    jurisdictionId?: string | null;
  },
): Promise<GovernmentForeclosureCaseListItem> {
  if (portal === "operator" && !body.jurisdictionId) {
    throw new Error("jurisdictionId is required to suggest case parties");
  }

  const result = await client.post<GovernmentForeclosureCaseListItem>({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/suggest-parties"
        : "/government/foreclosure-cases/suggest-parties",
    body: {
      county_fips: body.countyFips,
      case_number: body.caseNumber,
      ...(body.jurisdictionId ? { jurisdiction_id: body.jurisdictionId } : {}),
    },
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to suggest foreclosure case parties",
    );
  }

  return result.data as GovernmentForeclosureCaseListItem;
}

export async function suggestForeclosureCaseReview(
  portal: "government" | "operator",
  body: {
    countyFips: string;
    caseNumber: string;
    jurisdictionId?: string | null;
  },
): Promise<GovernmentForeclosureCaseListItem> {
  if (portal === "operator" && !body.jurisdictionId) {
    throw new Error("jurisdictionId is required to suggest case review");
  }

  const result = await client.post<GovernmentForeclosureCaseListItem>({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/suggest-review"
        : "/government/foreclosure-cases/suggest-review",
    body: {
      county_fips: body.countyFips,
      case_number: body.caseNumber,
      ...(body.jurisdictionId ? { jurisdiction_id: body.jurisdictionId } : {}),
    },
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to suggest foreclosure case review",
    );
  }

  return result.data as GovernmentForeclosureCaseListItem;
}

export async function updateForeclosureCaseReview(
  portal: "government" | "operator",
  body: {
    countyFips: string;
    caseNumber: string;
    reviewStatus?: ForeclosureCaseReviewStatus | null;
    reviewComment?: string | null;
    overrideOmtPartyId?: string | null;
    overrideAmsPartyId?: string | null;
    overrideRegistrantAccountId?: string | null;
    overrideStartDate?: string | null;
    overrideCaseNumber?: string | null;
  },
): Promise<ForeclosureCaseReview> {
  const payload: Record<string, unknown> = {
    county_fips: body.countyFips,
    case_number: body.caseNumber,
  };
  if (body.reviewStatus !== undefined) {
    payload.review_status = body.reviewStatus;
  }
  if (body.reviewComment !== undefined) {
    payload.review_comment = body.reviewComment;
  }
  if (body.overrideOmtPartyId !== undefined) {
    payload.override_omt_party_id = body.overrideOmtPartyId;
  }
  if (body.overrideAmsPartyId !== undefined) {
    payload.override_ams_party_id = body.overrideAmsPartyId;
  }
  if (body.overrideRegistrantAccountId !== undefined) {
    payload.override_registrant_account_id = body.overrideRegistrantAccountId;
  }
  if (body.overrideStartDate !== undefined) {
    payload.override_start_date = body.overrideStartDate;
  }
  if (body.overrideCaseNumber !== undefined) {
    payload.override_case_number = body.overrideCaseNumber;
  }

  const result = await client.patch<ForeclosureCaseReview>({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/review"
        : "/government/foreclosure-cases/review",
    body: payload,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to update foreclosure case review",
    );
  }

  return result.data as ForeclosureCaseReview;
}

export async function linkForeclosureCaseToLicense(
  portal: "government" | "operator",
  body: {
    caseNumber: string;
    licenseId: string;
    jurisdictionId?: string | null;
    countyFips?: string | null;
  },
): Promise<ForeclosureLicenseCandidate> {
  if (portal === "operator" && !body.jurisdictionId) {
    throw new Error("jurisdictionId is required to link a foreclosure case");
  }

  const result = await client.post<ForeclosureLicenseCandidate>({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/link-license"
        : "/government/foreclosure-cases/link-license",
    body: {
      case_number: body.caseNumber,
      license_id: body.licenseId,
      ...(body.jurisdictionId ? { jurisdiction_id: body.jurisdictionId } : {}),
      ...(body.countyFips ? { county_fips: body.countyFips } : {}),
    },
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to link foreclosure case",
    );
  }

  return result.data as ForeclosureLicenseCandidate;
}

export interface ForeclosureLicenseTypeOption {
  license_type_id: string;
  name: string;
}

export interface ForeclosureUnregisteredMatch {
  unregistered_id: string;
  license_type_id: string;
  license_type_name: string;
  effective_date: string;
  registrant_account_id: string | null;
  registrant_email: string | null;
  registrant_name: string | null;
  registrant_company_name: string | null;
  property_apn: string | null;
  property_address1: string | null;
  property_address2: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zipcode: string | null;
  match_score: number;
  match_reasons: string[];
  existing_case_number?: string | null;
}

export interface ForeclosureUnregisteredContext {
  matched: ForeclosureUnregisteredMatch | null;
  candidates?: ForeclosureUnregisteredMatch[];
  foreclosure_license_types: ForeclosureLicenseTypeOption[];
}

export async function getForeclosureUnregisteredMatch(
  portal: "government" | "operator",
  params: Omit<ForeclosureLicenseMatchQuery, "caseNumber"> & {
    caseNumber?: string | null;
    jurisdictionId?: string | null;
  },
): Promise<ForeclosureUnregisteredContext> {
  if (portal === "operator" && !params.jurisdictionId) {
    throw new Error(
      "jurisdictionId is required for operator unregistered match",
    );
  }

  const query: Record<string, string> = {};
  if (params.caseNumber) query.case_number = params.caseNumber;
  if (params.apn) query.apn = params.apn;
  if (params.propertyAddressFull) {
    query.property_address_full = params.propertyAddressFull;
  }
  if (params.propertyAddressCity) {
    query.property_address_city = params.propertyAddressCity;
  }
  if (params.propertyAddressState) {
    query.property_address_state = params.propertyAddressState;
  }
  if (params.propertyAddressZip) {
    query.property_address_zip = params.propertyAddressZip;
  }
  if (params.jurisdictionId) {
    query.jurisdiction_id = params.jurisdictionId;
  }

  const result = await client.get<ForeclosureUnregisteredContext>({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/unregistered-match"
        : "/government/foreclosure-cases/unregistered-match",
    query,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to load unregistered match",
    );
  }

  return result.data as ForeclosureUnregisteredContext;
}

export async function linkForeclosureCaseToUnregistered(
  portal: "government" | "operator",
  body: {
    caseNumber: string;
    unregisteredId: string;
    jurisdictionId?: string | null;
    countyFips?: string | null;
  },
): Promise<ForeclosureUnregisteredMatch> {
  if (portal === "operator" && !body.jurisdictionId) {
    throw new Error(
      "jurisdictionId is required to link an unregistered property",
    );
  }

  const result = await client.post<ForeclosureUnregisteredMatch>({
    url:
      portal === "operator"
        ? "/operator/foreclosure-cases/link-unregistered"
        : "/government/foreclosure-cases/link-unregistered",
    body: {
      case_number: body.caseNumber,
      unregistered_id: body.unregisteredId,
      ...(body.jurisdictionId ? { jurisdiction_id: body.jurisdictionId } : {}),
      ...(body.countyFips ? { county_fips: body.countyFips } : {}),
    },
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });

  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(
      err.detail ?? err.message ?? "Failed to link unregistered property",
    );
  }

  return result.data as ForeclosureUnregisteredMatch;
}

export interface BulkUnregisteredCaseRef {
  jurisdiction_id: string;
  county_fips: string;
  case_number: string;
}

export interface BulkUnregisteredLicenseType {
  license_type_id: string;
  name: string;
}

export interface BulkUnregisteredPreflightCase {
  jurisdiction_id: string;
  county_fips: string;
  case_number: string;
  eligible: boolean;
  skip_reason: string | null;
  foreclosure_license_types: BulkUnregisteredLicenseType[];
  suggested_omt_party_id: string | null;
  suggested_omt_display: string | null;
  suggested_ams_party_id: string | null;
  suggested_ams_display: string | null;
  omt_party_id: string | null;
  ams_party_id: string | null;
  registrant_account_id: string | null;
}

export type BulkPartyModeName = "blank" | "suggested" | "value";

export interface BulkPartyMode {
  mode: BulkPartyModeName;
  party_id: string | null;
}

export interface BulkCreateUnregisteredResult {
  jurisdiction_id: string;
  county_fips: string;
  case_number: string;
  status: "created" | "skipped" | "failed";
  skip_reason: string | null;
  detail: string | null;
  unregistered_id: string | null;
}

function foreclosureCasesUrl(portal: "government" | "operator", path: string) {
  return portal === "operator"
    ? `/operator/foreclosure-cases/${path}`
    : `/government/foreclosure-cases/${path}`;
}

async function postForeclosureCases<T>(
  portal: "government" | "operator",
  path: string,
  body: unknown,
  fallback: string,
): Promise<T> {
  const result = await client.post<T>({
    url: foreclosureCasesUrl(portal, path),
    body,
    parseAs: "json",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) {
    const err = result.error as { detail?: string; message?: string };
    throw new Error(err.detail ?? err.message ?? fallback);
  }
  return result.data as T;
}

export function preflightBulkCreateUnregistered(
  portal: "government" | "operator",
  cases: BulkUnregisteredCaseRef[],
) {
  return postForeclosureCases<{ cases: BulkUnregisteredPreflightCase[] }>(
    portal,
    "bulk-create-unregistered-preflight",
    { cases },
    "Failed to check selected cases",
  );
}

export function bulkCreateUnregisteredFromCases(
  portal: "government" | "operator",
  body: {
    license_type_id: string;
    registrant_account_id?: string | null;
    registrant_email?: string | null;
    answers_to_additional_questions?: Record<string, unknown> | null;
    omt: BulkPartyMode;
    ams: BulkPartyMode;
    pm: BulkPartyMode;
    cases: BulkUnregisteredCaseRef[];
  },
) {
  return postForeclosureCases<{ results: BulkCreateUnregisteredResult[] }>(
    portal,
    "bulk-create-unregistered",
    body,
    "Failed to create unregistered cases",
  );
}
